import { useCallback, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useFeatureFlagsContext } from '@shared/core/featureFlags';
import { useWebsitePublishCapabilityFeature } from '@shared/hooks/useWebsitePublishCapabilityFeature';
import { useWebsiteV2Feature } from '@shared/hooks/useWebsiteV2Feature';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addAction, addError } from '@shared/utils/logger';
import { withWindow } from '@shared/utils/withWindow';
import { useWebsiteV2Sites } from '@shared/utils/websiteV2/useWebsiteV2Sites';
import { useWebsiteV2SitePublishing } from '@shared/utils/websiteV2/useWebsiteV2SitePublishing';
import { getWebsiteV2EditorPath, getWebsiteV2PublishIntentPath } from '@shared/utils/websiteV2/websiteV2PublishLinks';
import { WebsiteV2PublishAllowanceDocument } from '@shared/utils/websiteV2/websiteV2Publish.graphql.generated';
import type { WebsiteV2PublishAllowanceQuery, WebsiteV2PublishAllowanceQueryVariables } from '@shared/utils/websiteV2/websiteV2Publish.graphql.generated';
import type { WebsitePublishOutcome, WebsitePublishState, WebsiteV2NotReadyReason } from './useWebsitePublishState.types';

/**
 * Whether the event's website is published, and the way to publish or unpublish it, for the publish
 * surfaces outside the website editor: the unpublished-website warning, the Share dialog's toggle,
 * card post-checkout.
 *
 * A Website V1 event reads and writes the legacy `Event.websiteState`
 * (`useWebsitePublishCapabilityFeature`). A Website V2 event reads its carbon sites, the truth for
 * it, and publishes through `useWebsiteV2SitePublishing`, as the editor does: one live site per
 * event, mirrored onto `websiteState`. The editor's Joy AI Website paywall can't open from here, so
 * an event that owes the purchase is handed off to the editor's publish intent
 * (doc/website-v2/publish-paywall.md, entry point 4), which opens it and publishes after payment —
 * the same hand-off as the draft guest view's banner.
 *
 * Not for the guest site's own gate: guests can't read carbon sites, and the server already tells
 * charm whether a V2 site is live (`useWebsiteV2GuestGate`).
 */
export const useWebsitePublishState = (): WebsitePublishState => {
  const { eventInfo, eventHandle } = useEventInfo();
  const eventId = eventInfo?.eventId ?? '';
  const legacy = useWebsitePublishCapabilityFeature();
  const websiteV2 = useWebsiteV2Feature();
  const isWebsiteV2 = websiteV2.value;
  const { sites, selectedSiteId, refetchSites, isLoading: isSitesLoading, error: sitesError } = useWebsiteV2Sites(eventId, { skip: !isWebsiteV2 });
  const { publishSite, unpublishSite } = useWebsiteV2SitePublishing({ eventId, sites, refetchSites });
  const apolloClient = useApolloClient();
  const { formatToUrl } = useFeatureFlagsContext();
  const [isUpdatingWebsiteV2, setIsUpdatingWebsiteV2] = useState(false);

  // The site guests get when it's published, and the one a publish here would make live: the same
  // active site every other surface resolves (useWebsiteV2Sites' contract).
  const activeSite = sites.find(site => site.id === selectedSiteId) ?? null;
  const hasLiveSite = sites.some(site => site.isPublished);
  const notReadyReason: WebsiteV2NotReadyReason | null = !isWebsiteV2 || hasLiveSite ? null : !activeSite ? 'noSite' : activeSite.status === 'ready' ? null : activeSite.status;

  // Full navigations: these surfaces live in other bundles (cards, ecards, the guest site) as well
  // as the admin app, and only the admin app has the editor route.
  const navigateTo = useCallback((path: string) => withWindow(w => w.location.assign(formatToUrl(path))), [formatToUrl]);

  const goToWebsiteEditor = useCallback(() => {
    if (eventHandle) navigateTo(getWebsiteV2EditorPath(eventHandle));
  }, [eventHandle, navigateTo]);

  const handOffToPaywall = useCallback(
    (siteId: string, reason: 'allowance' | 'refused') => {
      addAction('websitePublishStateHandedOffToPaywall', { eventId, siteId, reason });
      navigateTo(getWebsiteV2PublishIntentPath(eventHandle ?? '', siteId));
    },
    [eventId, eventHandle, navigateTo],
  );

  /** Up front, as the editor's Publish button does. Unknown (an error, or null) never counts as owed. */
  const isPurchaseOwed = useCallback(async () => {
    try {
      const { data } = await apolloClient.query<WebsiteV2PublishAllowanceQuery, WebsiteV2PublishAllowanceQueryVariables>({
        query: WebsiteV2PublishAllowanceDocument,
        variables: { eventId },
        fetchPolicy: 'network-only',
      });
      return data.carbonWebsiteAiAllowance.publishRequiresPurchase === true;
    } catch (error) {
      // The publish refusal still catches an owed purchase.
      addError(error, { context: 'useWebsitePublishState#isPurchaseOwed', eventId });
      return false;
    }
  }, [apolloClient, eventId]);

  const publishWebsiteV2 = useCallback(async (): Promise<WebsitePublishOutcome> => {
    if (hasLiveSite) return 'published';
    if (!activeSite || activeSite.status !== 'ready' || !eventHandle) return 'notReady';
    setIsUpdatingWebsiteV2(true);
    try {
      if (await isPurchaseOwed()) {
        handOffToPaywall(activeSite.id, 'allowance');
        return 'handedOff';
      }
      const result = await publishSite(activeSite.id);
      switch (result.error) {
        case undefined:
          return 'published';
        case 'siblingStillLive':
          // This site is live; guests get it. The stray sibling is the editor's to clean up.
          addAction('websitePublishStateSiblingStillLive', { eventId, siteId: activeSite.id });
          return 'published';
        case 'requiresPurchase':
          handOffToPaywall(activeSite.id, 'refused');
          return 'handedOff';
        case 'purchaseCheckUnavailable':
          return 'purchaseCheckUnavailable';
        default:
          return 'failed';
      }
    } finally {
      setIsUpdatingWebsiteV2(false);
    }
  }, [hasLiveSite, activeSite, eventHandle, eventId, isPurchaseOwed, handOffToPaywall, publishSite]);

  /** Takes down every live site, so nothing stays public, and the event goes to draft with the last. */
  const unpublishWebsiteV2 = useCallback(async () => {
    setIsUpdatingWebsiteV2(true);
    try {
      // From a FRESH list: a site published from another tab since this rendered must come down too.
      const fresh = await refetchSites().catch(error => {
        addError(error, { context: 'useWebsitePublishState#refetchSites', eventId });
        return sites;
      });
      // One at a time, not in parallel: each unpublish re-reads the list to set the event to draft once
      // none is live. A failure doesn't stop the rest, so every other live site still comes down.
      let isEverySiteDown = true;
      for (const site of fresh.filter(candidate => candidate.isPublished)) {
        try {
          await unpublishSite(site.id);
        } catch (error) {
          isEverySiteDown = false;
          addError(error, { context: 'useWebsitePublishState#unpublishWebsiteV2', eventId, siteId: site.id });
        }
      }
      return isEverySiteDown;
    } finally {
      setIsUpdatingWebsiteV2(false);
    }
  }, [refetchSites, sites, unpublishSite, eventId]);

  const { update: updateLegacy } = legacy.eventPublishedState;
  const publishLegacy = useCallback(async (): Promise<WebsitePublishOutcome> => {
    try {
      await updateLegacy(true);
      return 'published';
    } catch (error) {
      addError(error, { context: 'useWebsitePublishState#publishLegacy', eventId });
      return 'failed';
    }
  }, [updateLegacy, eventId]);

  const unpublishLegacy = useCallback(async () => {
    try {
      await updateLegacy(false);
      return true;
    } catch (error) {
      addError(error, { context: 'useWebsitePublishState#unpublishLegacy', eventId });
      return false;
    }
  }, [updateLegacy, eventId]);

  return useMemo<WebsitePublishState>(
    () =>
      isWebsiteV2
        ? {
            isWebsiteV2,
            isExposed: true,
            isPublished: hasLiveSite,
            isLoading: isSitesLoading,
            hasLoadError: !!sitesError,
            isUpdating: isUpdatingWebsiteV2,
            notReadyReason,
            publish: publishWebsiteV2,
            unpublish: unpublishWebsiteV2,
            goToWebsiteEditor,
          }
        : {
            isWebsiteV2,
            isExposed: legacy.eventExposure.value,
            isPublished: legacy.eventPublishedState.value,
            // The V2 flag decides which state this is, so it isn't known before the flag is.
            isLoading: websiteV2.isLoading || legacy.eventExposure.isLoading || legacy.eventPublishedState.isLoading,
            hasLoadError: false,
            isUpdating: legacy.eventPublishedState.isUpdating,
            notReadyReason: null,
            publish: publishLegacy,
            unpublish: unpublishLegacy,
            goToWebsiteEditor,
          },
    [
      isWebsiteV2,
      hasLiveSite,
      isSitesLoading,
      sitesError,
      isUpdatingWebsiteV2,
      notReadyReason,
      publishWebsiteV2,
      unpublishWebsiteV2,
      goToWebsiteEditor,
      legacy.eventExposure.value,
      legacy.eventExposure.isLoading,
      legacy.eventPublishedState.value,
      legacy.eventPublishedState.isLoading,
      legacy.eventPublishedState.isUpdating,
      websiteV2.isLoading,
      publishLegacy,
      unpublishLegacy,
    ],
  );
};
